// Global keyboard shortcuts of the advisor workstation.
//
// "/"      focuses the global search field of the top bar.
// "Escape" closes the overlays owned by the caller (drawer, menu, modal).
//
// HzTopbar ships its own "/" handler so it stays usable in isolation, in the
// component gallery for instance. Both handlers focus the very same input, so
// running them together is idempotent and never steals the focus twice.
import { onBeforeUnmount, onMounted } from 'vue';

/** Default selector of the global search input rendered by HzTopbar. */
export const SELECTEUR_RECHERCHE = '.hz-topbar__input';

export interface OptionsRaccourcis {
  /** Called on Escape, before any default behaviour. */
  readonly surEchappement?: () => void;
  /** Called on "/" instead of the default focus, when a screen owns its search. */
  readonly surRecherche?: () => void;
  /** Selector of the field focused by "/". */
  readonly selecteurRecherche?: string;
  /** Return false to suspend every shortcut, for example on the login screen. */
  readonly actif?: () => boolean;
}

/** True when the event target already captures raw keystrokes. */
function estZoneDeSaisie(cible: EventTarget | null): boolean {
  if (!(cible instanceof HTMLElement)) {
    return false;
  }
  return (
    cible.isContentEditable ||
    cible instanceof HTMLInputElement ||
    cible instanceof HTMLTextAreaElement ||
    cible instanceof HTMLSelectElement
  );
}

/** Move the caret into the global search field. Returns false when absent. */
export function focaliserRecherche(selecteur: string = SELECTEUR_RECHERCHE): boolean {
  const champ = document.querySelector(selecteur);
  if (!(champ instanceof HTMLInputElement)) {
    return false;
  }
  champ.focus();
  champ.select();
  return true;
}

/** Register the global shortcuts for the lifetime of the calling component. */
export function useRaccourcis(options: OptionsRaccourcis = {}): void {
  function surTouche(evenement: KeyboardEvent): void {
    if (options.actif !== undefined && !options.actif()) {
      return;
    }

    if (evenement.key === 'Escape') {
      options.surEchappement?.();
      return;
    }

    if (evenement.key !== '/' || evenement.metaKey || evenement.ctrlKey || evenement.altKey) {
      return;
    }
    if (estZoneDeSaisie(evenement.target)) {
      return;
    }

    if (options.surRecherche !== undefined) {
      evenement.preventDefault();
      options.surRecherche();
      return;
    }
    if (focaliserRecherche(options.selecteurRecherche ?? SELECTEUR_RECHERCHE)) {
      evenement.preventDefault();
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', surTouche);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', surTouche);
  });
}
